import React, { useCallback, useEffect } from 'react';

import { Controller, useFieldArray, useForm } from 'react-hook-form';
import dayjs from '~/timeInit';

import { Add as AddIcon, Delete as DeleteIcon, PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';

import {
  useCreateClaimCampaign,
  useListClaimRecords,
  useTestMatchCheck,
  useUpdateClaimCampaign,
} from '~/hooks/apis/claimCampaigns';
import useCurrentUser from '~/hooks/useCurrentUser';

import {
  CLAIM_ACTION_TYPES,
  CLAIM_CAMPAIGN_STATUSES,
  CLAIM_CAMPAIGN_TYPES,
  CLAIM_TARGET_TIERS,
  CLAIM_VALIDATOR_TYPES,
  type ClaimActionType,
  type ClaimCampaign,
  type ClaimCampaignStatus,
  type ClaimCampaignType,
  type ClaimMatchCheckType,
  type ClaimTargetTier,
  type ClaimValidatorType,
  type CreateClaimCampaignBody,
  EXTERNAL_API_METHODS,
  type ExternalApiMethod,
  type UpdateClaimCampaignBody,
} from '~/types/claimCampaignTypes';

interface CreateClaimCampaignDialogProps {
  open: boolean;
  onClose: () => void;
  /** When provided the dialog operates in edit mode. */
  campaign?: ClaimCampaign | null;
}

/** Form shape for a single action entry */
interface ActionFormEntry {
  type: ClaimActionType;
  /** TIER_UPGRADE fields */
  targetTier: ClaimTargetTier;
  remarks: string;
  /** BONUS_POINTS fields */
  bonusPoints: string;
  /** STATUS_CREDITS fields */
  statusCredits: string;
  bonusStatusCredits: string;
  /** Shared field for BONUS_POINTS and STATUS_CREDITS */
  activityDescription: string;
}

/** Full form data shape */
interface FormData {
  name: string;
  description: string;
  status: ClaimCampaignStatus;
  campaignType: ClaimCampaignType;
  validatorType: ClaimValidatorType;
  /** EXTERNAL_API validator fields */
  externalEndpoint: string;
  externalMethod: ExternalApiMethod;
  externalSuccessPath: string;
  externalErrorPath: string;
  externalTimeout: string;
  /** Approval fields (for any validator type) */
  requireApproval: boolean;
  evidenceFields: Array<{ name: string; label: string }>;
  /** MATCH campaign fields */
  bqQuery: string;
  eligibilityTemplate: string;
  /** Actions */
  actions: ActionFormEntry[];
  maxClaimsPerUser: string;
  startDate: string;
  endDate: string;
}

const DEFAULT_ACTION: ActionFormEntry = {
  type: 'BONUS_POINTS',
  targetTier: 'silver',
  remarks: '',
  bonusPoints: '',
  statusCredits: '',
  bonusStatusCredits: '',
  activityDescription: '',
};

const DEFAULT_VALUES: FormData = {
  name: '',
  description: '',
  status: 'DRAFT',
  campaignType: 'NORMAL',
  validatorType: 'PREGEN_CODE',
  externalEndpoint: '',
  externalMethod: 'POST',
  externalSuccessPath: '',
  externalErrorPath: '',
  externalTimeout: '5000',
  requireApproval: false,
  evidenceFields: [],
  bqQuery: '',
  eligibilityTemplate: '',
  actions: [{ ...DEFAULT_ACTION }],
  maxClaimsPerUser: '1',
  startDate: '',
  endDate: '',
};

/** Human-readable labels for validator types */
const VALIDATOR_LABELS: Record<ClaimValidatorType, string> = {
  PREGEN_CODE: 'Pre-generated Code',
  SHARED_CODE: 'Shared Code',
  EXTERNAL_API: 'External API',
  DIRECT: 'Direct',
};

/** Human-readable labels for action types */
const ACTION_TYPE_LABELS: Record<ClaimActionType, string> = {
  TIER_UPGRADE: 'Tier Upgrade',
  BONUS_POINTS: 'Bonus Points',
  STATUS_CREDITS: 'Status Credits',
};

/**
 * Converts a campaign's `actions` (Record<string, unknown>[]) back into ActionFormEntry[].
 */
function mapActionsToForm(actions: Record<string, unknown>[]): ActionFormEntry[] {
  return actions.map((a) => {
    const rawType = a.type;
    const type: ClaimActionType =
      rawType === 'TIER_UPGRADE' ? 'TIER_UPGRADE' : rawType === 'STATUS_CREDITS' ? 'STATUS_CREDITS' : 'BONUS_POINTS';
    const rawTier = a.targetTier;
    const targetTier: ClaimTargetTier = rawTier === 'gold' ? 'gold' : rawTier === 'platinum' ? 'platinum' : 'silver';
    return {
      type,
      targetTier,
      remarks: String(a.remarks ?? ''),
      bonusPoints: a.bonusPoints != null ? String(a.bonusPoints) : '',
      statusCredits: a.statusCredits != null ? String(a.statusCredits) : '',
      bonusStatusCredits: a.bonusStatusCredits != null ? String(a.bonusStatusCredits) : '',
      activityDescription: String(a.activityDescription ?? ''),
    };
  });
}

/**
 * Converts a ClaimCampaign entity into form default values for editing.
 */
function campaignToFormValues(campaign: ClaimCampaign): FormData {
  const vc = campaign.validatorConfig ?? {};
  const mc = campaign.matchCheckConfig;
  const method = vc.method;
  const evidenceFields = campaign.evidenceFields?.map((f) => ({ name: f.name, label: f.label })) ?? [];
  return {
    name: campaign.name,
    description: campaign.description ?? '',
    status: campaign.status,
    campaignType: campaign.type,
    validatorType: campaign.validatorType,
    externalEndpoint: String(vc.endpoint ?? ''),
    externalMethod: method === 'GET' ? 'GET' : 'POST',
    externalSuccessPath: String(vc.successPath ?? ''),
    externalErrorPath: String(vc.errorPath ?? ''),
    externalTimeout: vc.timeout != null ? String(vc.timeout) : '5000',
    requireApproval: campaign.requireApproval ?? false,
    evidenceFields,
    bqQuery: mc?.bqQuery ?? '',
    eligibilityTemplate: mc?.eligibilityTemplate ?? '',
    actions: campaign.actions.length > 0 ? mapActionsToForm(campaign.actions) : [{ ...DEFAULT_ACTION }],
    maxClaimsPerUser: String(campaign.maxClaimsPerUser),
    startDate: campaign.startDate ? dayjs(campaign.startDate).format('YYYY-MM-DDTHH:mm') : '',
    endDate: campaign.endDate ? dayjs(campaign.endDate).format('YYYY-MM-DDTHH:mm') : '',
  };
}

export default function CreateClaimCampaignDialog({ open, onClose, campaign }: CreateClaimCampaignDialogProps) {
  const isEditMode = !!campaign;

  const { data: claimsData } = useListClaimRecords(
    campaign?.id ?? '',
    { limit: 1 },
    {
      enabled: open && isEditMode,
    },
  );
  const fieldsLocked = isEditMode && (claimsData?.result?.pagination?.total ?? 0) > 0;

  const { user } = useCurrentUser();
  const createMutation = useCreateClaimCampaign();
  const updateMutation = useUpdateClaimCampaign();
  const testMatchMutation = useTestMatchCheck();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const { control, handleSubmit, reset, watch, getValues } = useForm<FormData>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'actions' });
  const {
    fields: evidenceFieldEntries,
    append: appendEvidenceField,
    remove: removeEvidenceField,
  } = useFieldArray({
    control,
    name: 'evidenceFields',
  });

  const campaignType = watch('campaignType');
  const validatorType = watch('validatorType');
  const bqQuery = watch('bqQuery');
  const eligibilityTemplate = watch('eligibilityTemplate');
  const requireApproval = watch('requireApproval');

  // Reset form when dialog opens — pre-fill with campaign data when editing
  useEffect(() => {
    if (open) {
      reset(campaign ? campaignToFormValues(campaign) : DEFAULT_VALUES);
    }
  }, [open, reset, campaign]);

  /**
   * Builds the API request body from the form data.
   */
  const buildPayload = useCallback((data: FormData): CreateClaimCampaignBody => {
    let validatorConfig: Record<string, unknown> = { type: data.validatorType };
    if (data.validatorType === 'EXTERNAL_API') {
      validatorConfig = {
        type: 'EXTERNAL_API',
        endpoint: data.externalEndpoint,
        method: data.externalMethod,
        successPath: data.externalSuccessPath,
        errorPath: data.externalErrorPath || undefined,
        timeout: Number(data.externalTimeout),
      };
    }

    let matchCheckType: ClaimMatchCheckType | undefined;
    let matchCheckConfig: Record<string, unknown> | undefined;
    if (data.campaignType === 'MATCH') {
      matchCheckType = 'BQ_QUERY';
      matchCheckConfig = {
        type: 'BQ_QUERY',
        bqQuery: data.bqQuery,
        eligibilityTemplate: data.eligibilityTemplate,
      };
    }

    const actions = data.actions.map((action) => {
      switch (action.type) {
        case 'TIER_UPGRADE':
          return {
            type: 'TIER_UPGRADE' as const,
            targetTier: action.targetTier,
            reasonCode: 'G',
            remarks: action.remarks,
          };
        case 'BONUS_POINTS':
          return {
            type: 'BONUS_POINTS' as const,
            bonusPoints: Number(action.bonusPoints),
            activityDescription: action.activityDescription,
          };
        case 'STATUS_CREDITS':
          return {
            type: 'STATUS_CREDITS' as const,
            statusCredits: Number(action.statusCredits),
            bonusStatusCredits: action.bonusStatusCredits ? Number(action.bonusStatusCredits) : undefined,
            activityDescription: action.activityDescription,
          };
      }
    });

    return {
      name: data.name.trim(),
      description: data.description.trim() || undefined,
      status: data.status,
      type: data.campaignType,
      validatorType: data.validatorType,
      validatorConfig,
      requireApproval: data.requireApproval,
      evidenceFields: data.requireApproval ? data.evidenceFields.filter((f) => f.name.trim()) : undefined,
      matchCheckType,
      matchCheckConfig,
      actions,
      maxClaimsPerUser: Number(data.maxClaimsPerUser),
      startDate: data.startDate ? dayjs(data.startDate).toISOString() : undefined,
      endDate: data.endDate ? dayjs(data.endDate).toISOString() : undefined,
    };
  }, []);

  const onSubmit = useCallback(
    async (data: FormData) => {
      if (data.actions.length === 0) return;
      try {
        if (isEditMode && campaign) {
          const payload = buildPayload(data);
          const updateBody: UpdateClaimCampaignBody = {
            name: payload.name,
            description: payload.description,
            status: payload.status,
            maxClaimsPerUser: payload.maxClaimsPerUser,
            matchCheckType: payload.matchCheckType,
            matchCheckConfig: payload.matchCheckConfig,
            evidenceFields: payload.evidenceFields,
            endDate: payload.endDate,
          };
          if (!fieldsLocked) {
            updateBody.validatorConfig = payload.validatorConfig;
            updateBody.actions = payload.actions;
            updateBody.startDate = payload.startDate;
          }
          await updateMutation.mutateAsync({ campaignId: campaign.id, body: updateBody });
        } else {
          await createMutation.mutateAsync(buildPayload(data));
        }
        onClose();
      } catch {
        // Error handled in mutation hooks
      }
    },
    [isEditMode, campaign, createMutation, updateMutation, buildPayload, onClose, fieldsLocked],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Claim Campaign' : 'Create Claim Campaign'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {fieldsLocked && (
            <Alert severity="info">
              Validator, actions, and start date cannot be changed after claims have been made.
            </Alert>
          )}

          {/* Basic info */}
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Name is required' }}
            render={({ field, fieldState: { error } }) => (
              <TextField {...field} label="Name" required fullWidth error={!!error} helperText={error?.message} />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => <TextField {...field} label="Description" fullWidth multiline rows={2} />}
          />

          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr 1fr' }} gap={2}>
            <Controller
              name="campaignType"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Campaign Type" fullWidth disabled={isEditMode}>
                  {CLAIM_CAMPAIGN_TYPES.map((ct) => (
                    <MenuItem key={ct} value={ct}>
                      {ct === 'NORMAL' ? 'Normal' : 'Match'}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <TextField {...field} select label="Status" fullWidth>
                  {CLAIM_CAMPAIGN_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />

            <Controller
              name="maxClaimsPerUser"
              control={control}
              rules={{
                required: 'Required',
                validate: (v) => Number(v) >= 1 || 'Must be at least 1',
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Max Claims Per User"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1 }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Box>

          {/* Dates */}
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Start Date"
                  type="datetime-local"
                  fullWidth
                  disabled={fieldsLocked}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
            <Controller
              name="endDate"
              control={control}
              rules={{
                required: requireApproval ? 'End date is required when CS Approval is enabled' : false,
                validate: (value) => {
                  const start = getValues('startDate');
                  if (start && value && dayjs(value).isBefore(dayjs(start))) {
                    return 'End date must be after start date';
                  }
                  return true;
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="End Date"
                  type="datetime-local"
                  fullWidth
                  required={requireApproval}
                  InputLabelProps={{ shrink: true }}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Box>

          <Divider />

          {/* Validator type */}
          <Typography variant="subtitle1" fontWeight={600}>
            Validator
          </Typography>
          <Controller
            name="validatorType"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Validator Type" fullWidth disabled={fieldsLocked}>
                {CLAIM_VALIDATOR_TYPES.map((vt) => (
                  <MenuItem key={vt} value={vt}>
                    {VALIDATOR_LABELS[vt]}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {/* External API config fields — shown only for EXTERNAL_API validator */}
          {validatorType === 'EXTERNAL_API' && (
            <Stack spacing={2} sx={{ pl: 2, borderLeft: 3, borderColor: 'divider' }}>
              <Controller
                name="externalEndpoint"
                control={control}
                rules={{ required: 'Endpoint is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Endpoint URL"
                    required
                    fullWidth
                    disabled={fieldsLocked}
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Controller
                  name="externalMethod"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} select label="HTTP Method" fullWidth disabled={fieldsLocked}>
                      {EXTERNAL_API_METHODS.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  name="externalTimeout"
                  control={control}
                  rules={{ required: 'Timeout is required', validate: (v) => Number(v) > 0 || 'Must be positive' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Timeout (ms)"
                      type="number"
                      fullWidth
                      disabled={fieldsLocked}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>
              <Controller
                name="externalSuccessPath"
                control={control}
                rules={{ required: 'Success path is required' }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="Success JSONPath"
                    required
                    fullWidth
                    disabled={fieldsLocked}
                    error={!!error}
                    helperText={error?.message || 'JSONPath expression to check for success in response'}
                  />
                )}
              />
              <Controller
                name="externalErrorPath"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Error JSONPath (optional)"
                    fullWidth
                    disabled={fieldsLocked}
                    helperText="JSONPath expression for error details in response"
                  />
                )}
              />
            </Stack>
          )}

          {/* Require Approval toggle + evidence fields */}
          <Controller
            name="requireApproval"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} disabled={isEditMode} />}
                label="Require CS Approval"
              />
            )}
          />

          {requireApproval && (
            <Stack spacing={2} sx={{ pl: 2, borderLeft: 3, borderColor: 'divider' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Evidence Fields</Typography>
                {!fieldsLocked && (
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => appendEvidenceField({ name: '', label: '' })}
                  >
                    Add Field
                  </Button>
                )}
              </Box>
              {evidenceFieldEntries.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No evidence fields — customers will submit free-form evidence.
                </Typography>
              )}
              {evidenceFieldEntries.map((ef, index) => (
                <Box key={ef.id} display="grid" gridTemplateColumns="1fr 1fr auto" gap={1} alignItems="center">
                  <Controller
                    name={`evidenceFields.${index}.name`}
                    control={control}
                    rules={{ required: 'Required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Field name"
                        size="small"
                        disabled={fieldsLocked}
                        error={!!error}
                        helperText={error?.message || 'e.g. bookingReference'}
                      />
                    )}
                  />
                  <Controller
                    name={`evidenceFields.${index}.label`}
                    control={control}
                    rules={{ required: 'Required' }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Display label"
                        size="small"
                        disabled={fieldsLocked}
                        error={!!error}
                        helperText={error?.message || 'e.g. Booking Reference'}
                      />
                    )}
                  />
                  {!fieldsLocked && (
                    <Tooltip title="Remove field">
                      <IconButton size="small" onClick={() => removeEvidenceField(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Stack>
          )}

          {/* Match configuration — shown only for MATCH campaigns */}
          {campaignType === 'MATCH' && (
            <>
              <Divider />
              <Typography variant="subtitle1" fontWeight={600}>
                Match Configuration
              </Typography>
              <Stack spacing={2} sx={{ pl: 2, borderLeft: 3, borderColor: 'divider' }}>
                <Controller
                  name="bqQuery"
                  control={control}
                  rules={{ required: 'BigQuery query is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="BigQuery Query"
                      multiline
                      rows={4}
                      required
                      fullWidth
                      disabled={fieldsLocked}
                      error={!!error}
                      helperText={
                        error?.message ||
                        'SQL query to fetch eligible customers. Must SELECT customer_bk or customer_id. Use IN UNNEST(@customerIds) to filter enrolled customers.'
                      }
                      inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                    />
                  )}
                />
                <Controller
                  name="eligibilityTemplate"
                  control={control}
                  rules={{ required: 'Eligibility template is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Eligibility Template"
                      multiline
                      rows={2}
                      required
                      fullWidth
                      disabled={fieldsLocked}
                      error={!!error}
                      helperText={
                        error?.message || 'Lodash template evaluated per row — return truthy to mark eligible'
                      }
                      inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
                    />
                  )}
                />

                {/* Test match check — reads query and template from form fields */}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={testMatchMutation.isPending ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                  disabled={testMatchMutation.isPending || !bqQuery || !eligibilityTemplate}
                  onClick={() =>
                    testMatchMutation.mutate({
                      bqQuery,
                      eligibilityTemplate,
                      customerId: user?.id_member ?? '',
                    })
                  }
                >
                  {testMatchMutation.isPending ? 'Running...' : 'Test Match Check'}
                </Button>

                {testMatchMutation.error && (
                  <Alert severity="error">
                    {testMatchMutation.error instanceof Error
                      ? testMatchMutation.error.message
                      : String(testMatchMutation.error)}
                  </Alert>
                )}

                {testMatchMutation.data &&
                  (() => {
                    const rows = testMatchMutation.data.result.rows.map((row, i) => ({ id: i, ...row }));
                    const columns: GridColDef[] =
                      rows.length > 0
                        ? Object.keys(testMatchMutation.data.result.rows[0]).map((key) => ({
                            field: key,
                            headerName: key,
                            flex: 1,
                            minWidth: 120,
                            renderCell: ({ value }) => {
                              if (key === 'eligible') {
                                return (
                                  <Chip
                                    label={value ? 'Yes' : 'No'}
                                    size="small"
                                    color={value ? 'success' : 'default'}
                                  />
                                );
                              }
                              return String(value ?? '');
                            },
                          }))
                        : [];

                    return rows.length > 0 ? (
                      <Box sx={{ height: 300 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {testMatchMutation.data.result.rowCount} row
                          {testMatchMutation.data.result.rowCount !== 1 ? 's' : ''} returned
                        </Typography>
                        <DataGridPro rows={rows} columns={columns} density="compact" disableRowSelectionOnClick />
                      </Box>
                    ) : (
                      <Alert severity="info">No rows returned. Check your query syntax.</Alert>
                    );
                  })()}
              </Stack>
            </>
          )}

          <Divider />

          {/* Actions */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              Actions
            </Typography>
            {!fieldsLocked && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => append({ ...DEFAULT_ACTION })}>
                Add Action
              </Button>
            )}
          </Box>

          {fields.map((field, index) => (
            <ActionFormSection
              key={field.id}
              control={control}
              index={index}
              disabled={fieldsLocked}
              onRemove={() => remove(index)}
            />
          ))}

          {fields.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              At least one action is required.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={isPending}>
          {isEditMode ? (isPending ? 'Saving...' : 'Save') : isPending ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Action form sub-section
// ---------------------------------------------------------------------------

interface ActionFormSectionProps {
  control: ReturnType<typeof useForm<FormData>>['control'];
  index: number;
  disabled?: boolean;
  onRemove: () => void;
}

/**
 * Renders dynamic form fields for a single action based on its type.
 */
function ActionFormSection({ control, index, disabled, onRemove }: ActionFormSectionProps) {
  return (
    <Stack spacing={2} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Action {index + 1}</Typography>
        {!disabled && (
          <Tooltip title="Remove action">
            <IconButton size="small" onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Controller
        name={`actions.${index}.type`}
        control={control}
        render={({ field }) => (
          <TextField {...field} select label="Action Type" fullWidth disabled={disabled}>
            {CLAIM_ACTION_TYPES.map((at) => (
              <MenuItem key={at} value={at}>
                {ACTION_TYPE_LABELS[at]}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <ActionDynamicFields control={control} index={index} disabled={disabled} />
    </Stack>
  );
}

interface ActionDynamicFieldsProps {
  control: ReturnType<typeof useForm<FormData>>['control'];
  index: number;
  disabled?: boolean;
}

/**
 * Renders the appropriate form fields based on the action type.
 * Uses Controller-based watching to re-render when the type changes.
 */
function ActionDynamicFields({ control, index, disabled }: ActionDynamicFieldsProps) {
  return (
    <Controller
      name={`actions.${index}.type`}
      control={control}
      render={({ field: { value: currentType } }) => {
        switch (currentType) {
          case 'TIER_UPGRADE':
            return (
              <Stack spacing={2}>
                <Controller
                  name={`actions.${index}.targetTier`}
                  control={control}
                  rules={{ required: 'Target tier is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      select
                      label="Target Tier"
                      fullWidth
                      disabled={disabled}
                      error={!!error}
                      helperText={error?.message}
                    >
                      {CLAIM_TARGET_TIERS.map((tier) => (
                        <MenuItem key={tier} value={tier}>
                          {tier.charAt(0).toUpperCase() + tier.slice(1)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                <Controller
                  name={`actions.${index}.remarks`}
                  control={control}
                  rules={{ required: 'Remarks are required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Remarks"
                      required
                      fullWidth
                      disabled={disabled}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Stack>
            );
          case 'BONUS_POINTS':
            return (
              <Stack spacing={2}>
                <Controller
                  name={`actions.${index}.bonusPoints`}
                  control={control}
                  rules={{
                    required: 'Bonus points is required',
                    validate: (v) => Number(v) > 0 || 'Must be greater than 0',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Bonus Points"
                      type="number"
                      required
                      fullWidth
                      disabled={disabled}
                      inputProps={{ min: 1 }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                <Controller
                  name={`actions.${index}.activityDescription`}
                  control={control}
                  rules={{ required: 'Activity description is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Activity Description"
                      required
                      fullWidth
                      disabled={disabled}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Stack>
            );
          case 'STATUS_CREDITS':
            return (
              <Stack spacing={2}>
                <Controller
                  name={`actions.${index}.statusCredits`}
                  control={control}
                  rules={{
                    required: 'Status credits is required',
                    validate: (v) => Number(v) > 0 || 'Must be greater than 0',
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Status Credits"
                      type="number"
                      required
                      fullWidth
                      disabled={disabled}
                      inputProps={{ min: 1 }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                <Controller
                  name={`actions.${index}.bonusStatusCredits`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Bonus Status Credits (optional)"
                      type="number"
                      fullWidth
                      disabled={disabled}
                      inputProps={{ min: 0 }}
                    />
                  )}
                />
                <Controller
                  name={`actions.${index}.activityDescription`}
                  control={control}
                  rules={{ required: 'Activity description is required' }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Activity Description"
                      required
                      fullWidth
                      disabled={disabled}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Stack>
            );
          default:
            return null;
        }
      }}
    />
  );
}
